/**
 * /decks/:id — the editor view.
 *
 * Three-column layout: slide rail (left) · server-rendered preview iframe
 * (center) · inspector (right). PR1 ships read-only inspectors; PR2
 * replaces them with per-layout edit forms; PR3 adds slide management
 * (add / duplicate / delete / drag-reorder).
 *
 * Wiring philosophy: the rail and inspector remain dumb-ish components.
 * This shell owns the selection state, the layout-picker modal state,
 * and the slide-management mutations (insert / delete / reorder). It
 * passes handlers down so each child can stay focused on its rendering.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useDeck,
  useDeleteSlide,
  useInsertSlide,
  useReorderSlides,
} from "../hooks";
import {
  newSlideFromTemplate,
  type SlideTemplate,
} from "../slideTemplates";
import { DeckEditorTopBar } from "./DeckEditorTopBar";
import { Inspector } from "./Inspector";
import { LayoutPickerModal } from "./LayoutPickerModal";
import { PreviewFrame } from "./PreviewFrame";
import { SlideRail } from "./SlideRail";

export function DeckEditor() {
  const params = useParams<{ deckId: string }>();
  const deckId = params.deckId;
  const navigate = useNavigate();
  const { data: deck, isLoading, isError, error } = useDeck(deckId);
  const [activeIdx, setActiveIdx] = useState(0);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);

  const slides = useMemo(() => deck?.deck_json.slides ?? [], [deck]);
  // Clamp activeIdx if the deck shrinks below the current selection.
  const clampedIdx = Math.min(activeIdx, Math.max(slides.length - 1, 0));

  // PR3 mutations — only meaningful once the deck has loaded but we
  // unconditionally instantiate so hook order stays stable across the
  // load/error early-return below.
  const insertSlide = useInsertSlide(deck?.id ?? "");
  const deleteSlide = useDeleteSlide(deck?.id ?? "");
  const reorderSlides = useReorderSlides(deck?.id ?? "");

  const slideManagementBusy =
    insertSlide.isPending ||
    deleteSlide.isPending ||
    reorderSlides.isPending;

  if (isLoading) {
    return (
      <ScreenShell>
        <p style={{ padding: "2rem", color: "var(--gray-9)" }}>Loading deck…</p>
      </ScreenShell>
    );
  }
  if (isError || !deck) {
    return (
      <ScreenShell>
        <ErrorBox
          title="Could not load this deck"
          message={error instanceof Error ? error.message : "Unknown error"}
          onBack={() => navigate("/decks")}
        />
      </ScreenShell>
    );
  }

  // Capture once after the narrowing guard above so TypeScript stops
  // worrying about deck being undefined inside the handlers below.
  const loadedDeck = deck;

  function handlePickLayout(template: SlideTemplate) {
    setLayoutPickerOpen(false);
    // Insert *after* the currently-selected slide so the new slide lands
    // where the user is likely paying attention. If nothing is selected
    // (e.g. an empty deck somehow), append.
    const insertAt = slides.length === 0 ? 0 : clampedIdx + 1;
    insertSlide.mutate(
      {
        payload: {
          at: insertAt,
          slide: newSlideFromTemplate(template),
        },
        ifMatchVersion: loadedDeck.version,
      },
      {
        onSuccess: ({ newIdx }) => {
          setActiveIdx(newIdx);
          // Scroll the rail to the new row on the next paint so the
          // freshly-rendered DOM has the right children to query.
          requestAnimationFrame(() => {
            const row = document.querySelector(
              `[data-testid='slide-rail-row-${newIdx}']`,
            );
            // Optional-chain the method call too: jsdom doesn't implement
            // scrollIntoView, so without the guard the rAF callback throws
            // an unhandled exception after the vitest test resolves and
            // fails the deploy's `Run tests` step. Real browsers always
            // have the method; this is purely a test-environment guard.
            row?.scrollIntoView?.({ block: "nearest" });
          });
        },
      },
    );
  }

  function handleDuplicateSlide(idx: number) {
    const source = slides[idx];
    if (!source) return;
    insertSlide.mutate(
      {
        payload: {
          at: idx + 1,
          slide: {
            section: source.section,
            slug: source.slug,
            titleHtml: source.titleHtml,
            meta: source.meta,
            bodyHtml: source.bodyHtml,
            renderPrefix: source.renderPrefix,
            renderSignature: source.renderSignature,
          },
        },
        ifMatchVersion: loadedDeck.version,
      },
      {
        onSuccess: ({ newIdx }) => setActiveIdx(newIdx),
      },
    );
  }

  function handleDeleteSlide(idx: number) {
    // Picked the previous slide as the new selection — or the first slide
    // if we just deleted the very first. Falls back to 0 if the deck ends
    // up empty (the route refuses this, but the clamp avoids a negative).
    const nextSelection = Math.max(0, idx - 1);
    deleteSlide.mutate(
      { slideIdx: idx, ifMatchVersion: loadedDeck.version },
      {
        onSuccess: () => setActiveIdx(nextSelection),
      },
    );
  }

  function handleReorder(newOrder: number[]) {
    // Track which original-idx is the currently-selected slide so we can
    // keep the same slide selected after the reorder lands.
    const selectedOriginalIdx = clampedIdx;
    const newSelectionIdx = newOrder.indexOf(selectedOriginalIdx);
    reorderSlides.mutate(
      { payload: { order: newOrder }, ifMatchVersion: loadedDeck.version },
      {
        onSuccess: () => {
          if (newSelectionIdx >= 0) setActiveIdx(newSelectionIdx);
        },
      },
    );
  }

  return (
    <ScreenShell>
      <DeckEditorTopBar deck={deck} />
      <div style={threePaneStyle}>
        <SlideRail
          slides={slides}
          activeIdx={clampedIdx}
          onSelect={(idx) => setActiveIdx(idx)}
          onAdd={() => setLayoutPickerOpen(true)}
          onReorder={handleReorder}
          disabled={slideManagementBusy}
        />
        <PreviewFrame
          deckId={deck.id}
          // Use the row's monotonic ``version`` as the cache buster — it
          // bumps on every PUT/PATCH so the iframe reloads automatically
          // when autosave lands.
          cacheTag={`v${deck.version}-${deck.deck_json.cacheTag}`}
        />
        <Inspector
          deck={deck}
          activeIdx={clampedIdx}
          onDuplicateSlide={handleDuplicateSlide}
          onDeleteSlide={handleDeleteSlide}
          slideManagementBusy={slideManagementBusy}
        />
      </div>
      <LayoutPickerModal
        open={layoutPickerOpen}
        onPick={handlePickLayout}
        onClose={() => setLayoutPickerOpen(false)}
      />
    </ScreenShell>
  );
}

function ScreenShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={shellStyle} data-testid="deck-editor-screen">
      {children}
    </div>
  );
}

function ErrorBox({
  title,
  message,
  onBack,
}: {
  title: string;
  message: string;
  onBack: () => void;
}) {
  return (
    <div
      style={{
        margin: "10vh auto",
        maxWidth: 480,
        textAlign: "center",
        color: "var(--gray-12)",
      }}
      data-testid="deck-editor-error"
    >
      <h2>{title}</h2>
      <p style={{ color: "var(--gray-10)" }}>{message}</p>
      <p>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "var(--gray-4)",
            border: "1px solid var(--gray-7)",
            padding: "0.4rem 0.9rem",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Back to deck list
        </button>
      </p>
      <p style={{ marginTop: "1rem", fontSize: "0.85em" }}>
        <Link to="/decks">/decks</Link>
      </p>
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: "var(--gray-1)",
  color: "var(--gray-12)",
};

const threePaneStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  minHeight: 0,
};
